/**
 * Компонент превью видео (YouTube и прямые ссылки)
 */

import { useState } from 'react';
import { getVideoType } from '@/utils/mediaParser';
import styles from './VideoGallery.module.css';

interface VideoPreviewProps {
  /** URL видео (YouTube или прямая ссылка) */
  url: string;
  /** Подпись к видео */
  caption?: string;
  /** Callback при клике */
  onClick: () => void;
}

/**
 * Извлекает YouTube Video ID из URL
 * Поддерживает форматы:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://youtube.com/watch?v=VIDEO_ID&other=params
 */
export function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/, // Just the ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return null;
}

/**
 * Получает URL thumbnail для YouTube видео
 */
export function getYouTubeThumbnail(videoId: string, quality: 'default' | 'mq' | 'hq' | 'sd' | 'maxres' = 'mq'): string {
  const qualityMap = {
    default: 'default',
    mq: 'mqdefault',
    hq: 'hqdefault',
    sd: 'sddefault',
    maxres: 'maxresdefault',
  };
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
}

/**
 * Превью видео с Play кнопкой (YouTube и прямые ссылки)
 */
export function VideoPreview({ url, caption, onClick }: VideoPreviewProps) {
  const [imageError, setImageError] = useState(false);
  const videoType = getVideoType(url);
  const videoId = extractYouTubeId(url);

  // Для YouTube видео
  if (videoType === 'youtube') {
    if (!videoId) {
      return (
        <div className={styles.previewError}>
          <span>⚠️ Неверная ссылка на видео</span>
        </div>
      );
    }

    const thumbnailUrl = getYouTubeThumbnail(videoId, imageError ? 'default' : 'mq');

    return (
      <div 
        className={styles.preview} 
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
      >
        <div className={styles.thumbnailWrapper}>
          <img
            src={thumbnailUrl}
            alt={caption || 'YouTube видео'}
            className={styles.thumbnail}
            loading="lazy"
            onError={() => setImageError(true)}
          />
          
          {/* Play button overlay */}
          <div className={styles.playButton}>
            <svg viewBox="0 0 68 48" className={styles.playIcon}>
              <path 
                d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" 
                fill="#f00"
              />
              <path d="M 45,24 27,14 27,34" fill="#fff" />
            </svg>
          </div>

          {/* Hover overlay */}
          <div className={styles.hoverOverlay}>
            <span>Нажмите для просмотра</span>
          </div>
        </div>

        {caption && (
          <div className={styles.caption}>{caption}</div>
        )}
      </div>
    );
  }

  // Для прямых видео файлов - показываем placeholder
  if (videoType === 'direct') {
    return (
      <div 
        className={styles.preview} 
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
      >
        <div className={styles.thumbnailWrapper}>
          {/* Placeholder для прямого видео */}
          <div className={styles.videoPlaceholder}>
            <svg 
              viewBox="0 0 68 48" 
              className={styles.playIcon}
              style={{ width: '68px', height: '48px', opacity: 0.8 }}
            >
              <rect width="68" height="48" fill="rgba(0,0,0,0.7)" rx="4" />
              <path 
                d="M 45,24 27,14 27,34" 
                fill="#fff" 
                stroke="#fff"
                strokeWidth="2"
              />
            </svg>
          </div>

          {/* Play button overlay */}
          <div className={styles.playButton}>
            <svg viewBox="0 0 68 48" className={styles.playIcon}>
              <path 
                d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" 
                fill="#968b74"
              />
              <path d="M 45,24 27,14 27,34" fill="#fff" />
            </svg>
          </div>

          {/* Hover overlay */}
          <div className={styles.hoverOverlay}>
            <span>Нажмите для просмотра</span>
          </div>
        </div>

        {caption && (
          <div className={styles.caption}>{caption}</div>
        )}
      </div>
    );
  }

  // Неизвестный тип видео
  return (
    <div className={styles.previewError}>
      <span>⚠️ Неподдерживаемый формат видео</span>
    </div>
  );
}

